import {
  Box,
  Button,
  Paper,
  Typography,
} from "@mui/material";
import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataToolbar from "../../components/common/DataToolbar";
import BackdropLoader from "../../components/common/BackdropLoader";
import type { AppDispatch } from "../../store";
import {
  setDateRange,
  setPage,
  setRowsPerPage,
  setSearchTerm,
} from "../../store/slices/RuletableSlice";
import { useNavigate } from "react-router-dom";
import { useGetAllRulesQuery } from "../../api/services/ruleApi";
import { Delete as DeleteIcon } from "@mui/icons-material";
import Swal from "sweetalert2";
import { useDeleteRuleMutation } from "../../api/services/ruleApi";
import DataTable from "../../components/common/DataTable";

export interface RuleItem {
  id: string;
  no_of_users: string;
  workCenter: string;
  materialCategory: string;
  approvalLevel: string;
}

const RuleScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { searchTerm, dateRange, rowsPerPage, currentPage } = useSelector(
    (state: any) => state.ruleTable
  );
  const navigate = useNavigate();
  //  Backend Fetch
  const { data, isLoading, isFetching } = useGetAllRulesQuery({
    page: currentPage,
    limit: rowsPerPage,
    q: searchTerm,
  });

  const rules = data?.rules || [];
  const pagination = data?.pagination || { total: 0, totalPages: 1 };

  useEffect(() => {
    dispatch(setPage(1));
  }, [rowsPerPage]);

  useEffect(() => {
    if (currentPage > pagination.totalPages) {
      dispatch(setPage(pagination.totalPages || 1));
    }
  }, [pagination.totalPages]);

  /* ---------------------------------------------------------------------------------------- */

  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // map data
  const ruleTableData = useMemo(() => {
    return rules.map((rule) => ({
      id: String(rule.ID),
      plant: rule.plant?.PLANTDISPLAYNAME,
      no_of_users: String(rule.members?.length || 0),
      workCenter: rule.WORK_CENTER,
      materialCategory: rule.MATERIAL_CATEGORY,
      approvalLevel: `Level ${rule.APPROVAL_LEVEL}`,
    }));
  }, [rules]);

  // sorting
  const sortedRuleData = useMemo(() => {
    if (!sortField || !sortOrder) return ruleTableData;

    return [...ruleTableData].sort((a: any, b: any) => {
      if (sortOrder === "asc") return a[sortField] > b[sortField] ? 1 : -1;
      return a[sortField] < b[sortField] ? 1 : -1;
    });
  }, [ruleTableData, sortField, sortOrder]);

  const [deleteRule] = useDeleteRuleMutation();

  const handleDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This rule will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteRule(Number(id)).unwrap();
          Swal.fire("Deleted!", "Rule has been removed.", "success");
        } catch (err: any) {
          Swal.fire(
            "Error",
            err?.data?.message || "Unable to delete rule",
            "error"
          );
        }
      }
    });
  };

  const renderAction = (row: any) => {
    return (
      <DeleteIcon
        sx={{ color: "red", cursor: "pointer" }}
        onClick={() => handleDelete(row.id)}
      />
    );
  };

  const columns = [
    { key: "plant", label: "Plant Name", sortable: true },
    { key: "no_of_users", label: "No of Users", sortable: true },
    { key: "workCenter", label: "Work Center", sortable: true },
    { key: "materialCategory", label: "Material Category", sortable: true },
    { key: "approvalLevel", label: "Approval Level", sortable: true },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <BackdropLoader openStates={isLoading || isFetching} />
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Rule Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Set and manage rules for how spare part requests work in the system.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#002060",
              color: "#fff",
              borderRadius: 3,
              textTransform: "none",
              fontSize: 12,
              mt: 1,
            }}
            onClick={() => navigate("/rule/create")}
          >
            Create Rule
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#002060",
              color: "#fff",
              borderRadius: 3,
              textTransform: "none",
              fontSize: 12,
              mt: 1,
            }}
            onClick={() => navigate("/approver/create")}
          >
            Assign Approver
          </Button>
        </Box>
      </Box>

      {/* Toolbar + Pagination */}
      <DataToolbar
        searchTerm={searchTerm}
        setSearchTerm={(v) => dispatch(setSearchTerm(v))}
        dateRange={dateRange}
        setDateRange={(v) => dispatch(setDateRange(v))}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={(v) => dispatch(setRowsPerPage(v))}
        currentPage={currentPage}
        setCurrentPage={(v) => dispatch(setPage(v))}
        totalPages={pagination.totalPages}
      />

      <Paper variant="outlined">
        <DataTable
          columns={columns}
          data={sortedRuleData}
          getRowId={(row) => row.id}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
          emptyMessage="No rules found"
          actionRenderer={renderAction}
        />
      </Paper>
    </Box>
  );
};

export default RuleScreen;
